'use client';

import { useState, useRef, useEffect } from 'react';
import { UploadSimple, X, WarningCircle } from '@phosphor-icons/react';

import { showToast } from '../../../../../utils/toast';
import { MAX_IMAGES, prepareImages } from '../../../../../utils/imageCompression';
import { cn } from '../../../../../design-system/lib/cn';
import { IconButton, SectionTitle } from '../../../../../design-system/components/desktop';
import { Icon } from '../../../../../design-system/components/Icon';
import { AuthError } from '../../../../../design-system/patterns/auth';
import { WizardPanel } from '../../wizard';

/**
 * Step 2, photos.
 *
 * No container/view split: this step talks to no API. Files are held in
 * `formData.images` and only leave the browser when the wizard posts its
 * multipart body on the review step, which is why there is no progress bar
 * here and no per-file upload state to show. The rejection list exists because
 * a toast is gone in four seconds and "which of my six files did it refuse" is
 * a question the seller still has after that.
 *
 * Photos are downscaled here, on selection, rather than at post time. Raw
 * camera photos put 15-25MB behind the single multipart POST that creates the
 * listing, which a mobile uplink could not push inside the client's 30s
 * timeout — the request was aborted mid-body and nothing was ever created.
 * Compressing on selection also means the previews and the "N MB" captions
 * below show the seller what will actually be sent. See
 * `utils/imageCompression`.
 *
 * The dropzone is a real `<button>`, so Enter and Space open the picker and the
 * hidden `<input type="file">` stays the only file control in the tree. It used
 * to be a `<div onClick>`, reachable by pointer only.
 */
const UploadImagesStep = ({
  formData,
  updateFormData,
  errors = {},
  titleRef,
  // Seeds for the state explorer only. The wizard passes neither, so in the
  // app both start where they always did: not dragging, nothing rejected.
  initialDragActive = false,
  initialRejected = [],
}) => {
  const [dragActive, setDragActive] = useState(initialDragActive);
  const [previews, setPreviews] = useState([]);
  const [rejected, setRejected] = useState(initialRejected);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) {
      return;
    }

    // Decoding and re-encoding several full-resolution photos takes a visible
    // moment on a mid-range phone. Without this flag the seller can reach Next
    // while `formData.images` is still half-filled.
    setProcessing(true);
    try {
      const { accepted, refusals } = await prepareImages(fileArray, {
        existingCount: formData.images.length,
      });

      setRejected(refusals);
      refusals.forEach((line) => showToast.error(line));

      if (accepted.length === 0) {
        showToast.warning(
          `No photos were added. Use JPG, JPEG or PNG files, up to ${MAX_IMAGES} per listing.`
        );
        return;
      }

      showToast.success(
        `${accepted.length} image${accepted.length > 1 ? 's' : ''} uploaded successfully!`
      );

      // Previews are rebuilt from `formData.images` by the effect below, so
      // there is nothing to append here.
      updateFormData('images', [...formData.images, ...accepted]);
    } finally {
      setProcessing(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeImage = (indexToRemove) => {
    const newImages = formData.images.filter((_, index) => index !== indexToRemove);
    const newPreviews = previews.filter((_, index) => index !== indexToRemove);

    updateFormData('images', newImages);
    setPreviews(newPreviews);

    // Revoke object URL to prevent memory leaks
    if (previews[indexToRemove]) {
      URL.revokeObjectURL(previews[indexToRemove].url);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    if (formData.images && formData.images.length > 0) {
      const imagePreviews = formData.images.map((file) => ({
        file,
        url: typeof file === 'string' ? file : URL.createObjectURL(file),
        id: Date.now() + Math.random(),
      }));
      setPreviews(imagePreviews);
    } else {
      setPreviews([]);
    }
  }, [formData.images]);

  return (
    <WizardPanel
      ref={titleRef}
      step={2}
      title="Photos"
      sub="Photograph the front and the back of the card you are actually sending. Photos are what a buyer judges condition on, so shoot them flat, in daylight, without a sleeve."
    >
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <button
          type="button"
          className={cn('dsa-cl-drop', dragActive && 'is-over')}
          onClick={openFileDialog}
          disabled={processing}
          aria-busy={processing}
        >
          <Icon as={UploadSimple} size="xl" className="dsa-cl-drop__glyph" />
          <span className="dsa-cl-drop__title">{processing ? 'Adding photos…' : 'Add photos'}</span>
          <span className="dsa-cl-drop__sub">Drag and drop images here, or click to browse</span>
          <span className="dsa-cl-drop__sub">
            JPG, JPEG or PNG, up to {MAX_IMAGES} photos. Large photos are resized automatically.
          </span>
        </button>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,image/jpeg,image/png"
          onChange={handleFileInput}
          disabled={processing}
          className="dsk-sr-only"
          aria-label="Choose card photos"
        />
      </div>

      <AuthError>{errors.images}</AuthError>

      {rejected.length ? (
        <ul className="dsa-cl-rejects">
          {rejected.map((line) => (
            <li key={line} className="dsa-cl-reject">
              <Icon as={WarningCircle} size="sm" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {previews.length > 0 ? (
        <div>
          <SectionTitle>Attached photos ({previews.length})</SectionTitle>
          <div className="dsa-cl-photos" style={{ marginTop: '12px' }}>
            {previews.map((preview, index) => (
              <div key={preview.id} className="dsa-cl-photo">
                {/* No "photo" in the alt. It read "Card photo N", and a screen
                    reader already announces an <img> as an image, so the word
                    is said twice — which is why jsx-a11y/img-redundant-alt
                    (warn, from eslint-config-react-app) fires on it on every
                    compile. The index survives because it is the only thing
                    that tells the two thumbnails apart, and it matches the
                    remove button's label beside it. */}
                <img src={preview.url} alt={`Card ${index + 1}`} className="dsa-cl-photo__img" />
                {index === 0 ? <span className="dsa-cl-photo__cover">Cover</span> : null}
                <IconButton
                  className="dsa-cl-photo__remove"
                  label={`Remove photo ${index + 1}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    removeImage(index);
                  }}
                >
                  <Icon as={X} size="sm" />
                </IconButton>
                <p className="dsa-cl-photo__cap">
                  {preview.file?.name}
                  {preview.file?.size
                    ? ` · ${(preview.file.size / 1024 / 1024).toFixed(2)} MB`
                    : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </WizardPanel>
  );
};

export default UploadImagesStep;
